import { RcFile } from 'antd/lib/upload';
import { noop } from 'lodash';
import * as docxPreview from 'docx-preview';

import {
  AUDIO_FILE_EXTENSIONS,
  DOC_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
  VIDEO_FILE_EXTENSIONS,
} from '@doltech/utils/lib/file.types';
import { apis } from '@doltech/core/lib/api/api.config';
import { documentPartToHtml } from '@doltech/utils/lib/docx-to-editor';
import { FileTypeEnum, handleUploadBlob } from '@doltech/utils/lib/file-upload';

export const ACCEPT_IMAGE_ONLY = 'image/*';
export const ACCEPT_MEDIA_ONLY = [
  ...VIDEO_FILE_EXTENSIONS,
  ...AUDIO_FILE_EXTENSIONS,
  ...IMAGE_FILE_EXTENSIONS,
].join(',');
export const ACCEPT_VIDEO_ONLY = VIDEO_FILE_EXTENSIONS.join(',');
export const ACCEPT_AUDIO_ONLY = AUDIO_FILE_EXTENSIONS.join(','); // Why add specific extensions? Because issue on IOS not support 'audio/*'
export const ACCEPT_SUBTITLE_ONLY = '.vtt,.srt';
export const ACCEPT_EXCEL_ONLY =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';
export const ACCEPT_PDF_ONLY = 'application/pdf';
export const ACCEPT_DOC_ONLY = DOC_FILE_EXTENSIONS.join(',');

export const getFileTypeFromMimeType = (mimeType: string): FileTypeEnum => {
  if (mimeType.includes('audio')) return FileTypeEnum.AUDIO;
  if (mimeType.includes('image')) return FileTypeEnum.IMAGE;
  if (mimeType.includes('video')) return FileTypeEnum.VIDEO;
  if (mimeType === 'application/pdf') return FileTypeEnum.PDF;
  return FileTypeEnum.OTHER;
};

export const getFileTypeFromFileAccept = (accept: string): FileTypeEnum => {
  if (accept === ACCEPT_AUDIO_ONLY) return FileTypeEnum.AUDIO;
  if (accept === ACCEPT_IMAGE_ONLY) return FileTypeEnum.IMAGE;
  if (accept === ACCEPT_VIDEO_ONLY) return FileTypeEnum.VIDEO;
  if (accept === ACCEPT_PDF_ONLY) return FileTypeEnum.PDF;
  return FileTypeEnum.OTHER;
};

export interface ProgressInput {
  percent: number;
}

interface HandleUploadWithApiOptions {
  usePrivate?: boolean;
  file: RcFile;
  onUploadProgress?: ({ total, loaded }: { total: number; loaded: number }) => void;
}

export const handleUploadWithApi = async (options: HandleUploadWithApiOptions, retryTime = 3) => {
  const { file } = options;
  const blob = new Blob([file], { type: file.type });
  return handleUploadBlob(blob, file, options);
};

export const handleUploadWithApiCustomRequestForAntDesign = ({
  file,
  onError,
  onProgress,
  onSuccess,
}) => {
  handleUploadWithApi({
    file,
    usePrivate: true,
    onUploadProgress: ({ total, loaded }) => {
      onProgress({ percent: Math.round((loaded / total) * 100).toFixed(2) }, file);
    },
  })
    .then((res) => onSuccess(res, file))
    .catch(onError);
};

export const handlePublicUploadWithApiCustomRequestForAntDesign = ({
  file,
  onError,
  onProgress,
  onSuccess,
  onStart,
  onFinish,
}: {
  file?: any;
  onError?: (err: any) => void;
  onProgress?: (progressInput: any, file: any) => void;
  onSuccess?: (res: any) => void;
  onStart?: () => void;
  onFinish?: () => void;
}) => {
  onStart?.();
  handleUploadWithApi({
    file,
    usePrivate: false,
    onUploadProgress: ({ total, loaded }) => {
      onProgress?.({ percent: Math.round((loaded / total) * 100).toFixed(2) }, file);
    },
  })
    .then((res) => {
      onSuccess?.(res);
      onFinish?.();
    })
    .catch((err) => {
      onError?.(err);
      onFinish?.();
    });
};

export const handleUploadFileForResource = ({ file, usePrivate = false, onProgress = noop }) => {
  return handleUploadWithApi({
    file,
    usePrivate,
    onUploadProgress: ({ total, loaded }) => {
      onProgress?.({ percent: Math.round((loaded / total) * 100).toFixed(2) }, file);
    },
  });
};

export const convertDocToPdf = async (file: any) => {
  const formData = new FormData();
  formData.append('files', file);
  const { data } = await apis.gotenberg.post('/forms/libreoffice/convert', formData, {
    responseType: 'blob',
  });

  return data;
};

export const convertDocToHtml = async (file: any) => {
  const result = await docxPreview.parseAsync(file);
  const html = await documentPartToHtml({
    documentPart: result?.documentPart,
    stylesPart: result?.stylesPart,
    partsMap: result?.partsMap,
  });
  return html;
};

export const handleUploadDocFile = async ({
  file,
  onError,
  onProgress,
  onSuccess,
  onPreviewFileUploadSuccess,
  onFinish,
  enablePreviewConvert = false,
}) => {
  onProgress?.({ percent: 1 }, file);

  handlePublicUploadWithApiCustomRequestForAntDesign({
    file,
    onError,
    onProgress,
    onSuccess,
    onFinish,
  });

  if (enablePreviewConvert) {
    const blob: any = await convertDocToPdf(file);
    const previewFile = new File([blob], `${file.name}.pdf`, {
      type: 'application/pdf',
    });
    handlePublicUploadWithApiCustomRequestForAntDesign({
      file: previewFile,
      onSuccess: (res) => onPreviewFileUploadSuccess(res?.file),
      onError,
    });
  }
};
